import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Home, Package, Settings, Sun, Moon, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import logo from '../assets/AITemizeIT Without Text.png';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/items', label: 'Items', icon: Package },
    { path: '/admin', label: 'Admin', icon: Settings },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-900 transition-colors duration-200">
      {/* Header */}
      <header className="bg-white dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={logo} 
                alt="AItemizeIT Logo" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                  AItemizeIT
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Price Engine
                </p>
              </div>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{item.label}</span>
                  </Link>
                );
              })}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors ml-2"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )}
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={logo} alt="AItemizeIT" className="w-6 h-6" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                AItemizeIT Price Engine
              </span>
            </div>
            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              AI-Powered Household Item Valuation • Secure & Reliable
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <button
                type="button"
                onClick={() => setShowDisclaimer(true)}
                className="hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
              >
                Pricing &amp; Matching Disclaimer
              </button>
              <button
                type="button"
                onClick={() => setShowPrivacy(true)}
                className="hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
              >
                Privacy
              </button>
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
              >
                Terms
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Pricing Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 max-w-xl w-full mx-4 p-6 relative">
            <button
              type="button"
              onClick={() => setShowDisclaimer(false)}
              className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Close disclaimer"
            >
              <X className="w-4 h-4" />
            </button>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Pricing &amp; Matching Disclaimer
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              AItemizeIT Price Engine provides indicative price ranges for household items using external
              data sources and automated matching. These prices are estimates only and may not reflect the
              exact make, model, age, condition, or market availability of your specific item.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li>
                Prices are typically shown in South African Rand (ZAR) unless otherwise indicated and may be
                converted from other currencies.
              </li>
              <li>
                Item matching is performed using AI and search heuristics and may occasionally select
                comparable or similar products rather than an exact match.
              </li>
              <li>
                Values are for informational purposes only and should not be treated as formal valuations,
                financial advice, or binding insurance replacement values.
              </li>
              <li>
                Always review the suggested prices against your own documentation, invoices, and insurer
                guidelines before making decisions.
              </li>
            </ul>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              By using this application you acknowledge that all pricing information is provided "as is" and
              that AItemizeIT and its maintainers are not liable for any loss, claim, or decision arising
              from the use of these estimates.
            </p>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 max-w-2xl w-full mx-4 p-6 relative">
            <button
              type="button"
              onClick={() => setShowPrivacy(false)}
              className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Close privacy policy"
            >
              <X className="w-4 h-4" />
            </button>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Privacy Policy</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              AItemizeIT Price Engine stores only the data required to provide item lookups, pricing
              estimates, and audit trails for admin actions. We do not sell or share your data with
              third parties for marketing purposes.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li>
                Usage data such as search queries and item views may be logged to improve system
                accuracy and performance.
              </li>
              <li>
                Admin activity (such as manual price edits) may be recorded in update logs to provide
                traceability and auditability.
              </li>
              <li>
                Authentication and authorization are handled by Supabase; your credentials are managed
                by Supabase Auth, not stored directly by this application.
              </li>
              <li>
                Access to production data is restricted to authorized administrators, and all access
                should follow your organization&apos;s security policies.
              </li>
            </ul>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              For a formal privacy statement or data processing agreement, please refer to your
              organization&apos;s policies or contact the system owner.
            </p>
          </div>
        </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl border border-gray-200 dark:border-dark-700 max-w-2xl w-full mx-4 p-6 relative">
            <button
              type="button"
              onClick={() => setShowTerms(false)}
              className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Close terms and conditions"
            >
              <X className="w-4 h-4" />
            </button>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Terms &amp; Conditions</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              By using AItemizeIT Price Engine you agree that the service is provided on a best-effort
              basis and that all price information is indicative only. You remain responsible for
              verifying any values used for insurance, finance, or procurement decisions.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li>
                The application is intended for internal use to support household item valuation and
                related workflows.
              </li>
              <li>
                No guarantee is provided that the service will be continuously available or free from
                errors, interruptions, or data discrepancies.
              </li>
              <li>
                You must not attempt to bypass access controls, scrape data at scale, or misuse the
                service in ways that violate applicable laws or your organization&apos;s policies.
              </li>
              <li>
                The maintainers are not liable for any loss, damage, or cost arising from decisions
                made based on the pricing information provided by this tool.
              </li>
            </ul>
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              These terms are a high-level guidance summary. For contractual terms, SLAs, and legal
              agreements, please refer to your organization&apos;s official documentation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
